import { useGetIdentity, useGetList, useStore, useTranslate } from "ra-core";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { TasksListContent } from "../tasks/TasksListContent";
import type { Task as TaskRecord } from "../types";
import { queueToShow } from "./followUpsRules";
import { ALL_TASKS_PATH } from "./paths";
import { TASK_VIEWS, TASKS_VIEW_KEY } from "./taskLists";
import { TaskRows } from "./TaskRows";

/**
 * Every task, mine, open and completed.
 *
 * WHY DESKTOP HAD NO SUCH SCREEN AT ALL
 * -------------------------------------
 * `root/CRM.tsx` registered `<Resource name="tasks" />` with no list, show or
 * edit, while the MOBILE block registered `list={MobileTasksList}`. So the
 * bottom bar's Tasks slot opened a screen on a phone and `#/tasks` resolved to
 * nothing on a laptop -- and there was no route to a single task either, which
 * is why the lifecycle screen printed a dead-end sentence for a closed task
 * instead of a link (atomic#15).
 *
 * WHY "COMPLETED" IS A VIEW AND NOT A SECTION
 * -------------------------------------------
 * The two halves answer different questions and are worked at different times.
 * Open is the queue: what is on me today. Completed is the audit trail: what
 * did we actually do, and when. Stacking them puts a hundred finished rows
 * under the six that are not, on the screen most likely to be opened in a
 * hurry -- and the owner's sentence separates them too ("a place to see all
 * open/completed tasks").
 *
 * Tabs rather than a filter control, matching `FollowUps` one section over:
 * this app already answers "show me a different slice of the same queue" with
 * a tab strip, and a second idiom for the same question is one more thing to
 * learn for no gain.
 *
 * WHY THE SCOPE STAYS `sales_id`
 * ------------------------------
 * "All open/completed tasks from any contact" is about CONTACTS, not about
 * other people's work: the existing list is already scoped to the signed-in
 * user, and widening it here would silently turn a personal queue into a
 * team-wide one -- a change nobody asked for, on a screen people use to decide
 * what to do next. The per-CONTACT question, which genuinely does span
 * everybody, has its own page at /contacts/:id/tasks and carries no sales_id
 * filter at all.
 */

/**
 * How many completed tasks the queue reads.
 *
 * A ceiling rather than a page: `tasks` holds 69 rows, so this is far past
 * every real case today, and a "load more" control over a list nobody can
 * exhaust is a control that is never exercised and therefore never known to
 * work. Revisit it as a paging control when the count approaches this number,
 * which the footer below says out loud.
 */
const COMPLETED_TASKS_FETCHED = 200;

const VIEW_LABEL: Record<(typeof TASK_VIEWS)[number], string> = {
  open: "Open",
  completed: "Completed",
};

/** The screen itself, without either platform's chrome around it. */
const AllTasksBody = () => {
  /*
   * The chosen view is in the app's PERSISTED store, not in component state.
   * This screen is worked one task at a time -- open the contact, do the
   * thing, come back -- and with `useState` every one of those returns drops
   * you back on Open. Somebody auditing what was done last week would
   * re-select Completed after every single row. That is atomic#13.
   *
   * `chosen` is what the store HOLDS and may name a view this build does not
   * have; `queueToShow` checks it against the views that exist now. See the
   * long note in followUpsRules.ts on why a stored value is a suggestion.
   */
  const [chosen, setChosen] = useStore<string>(TASKS_VIEW_KEY, TASK_VIEWS[0]);

  return (
    <div className="flex flex-col gap-3">
      <Tabs value={queueToShow(chosen, TASK_VIEWS)} onValueChange={setChosen}>
        <TabsList>
          {TASK_VIEWS.map((view) => (
            <TabsTrigger key={view} value={view}>
              {VIEW_LABEL[view]}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="open" className="pt-3">
          {/*
            UPSTREAM'S OWN LIST, unchanged: the due-date sections, the
            sales_id scope and the five-minute grace are all already right
            here, and re-implementing them would be a second definition of
            "overdue" for the sake of owning the markup.
          */}
          <TasksListContent />
        </TabsContent>

        <TabsContent value="completed" className="pt-3">
          <CompletedTasks />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const CompletedTasks = () => {
  const { identity } = useGetIdentity();

  const { data, total, isPending, error } = useGetList<TaskRecord>(
    "tasks",
    {
      // CARRIES THE PREDICATE, so PostgREST does the filtering rather than the
      // browser -- and so this read can never accidentally show open work
      // under a heading that says Completed.
      filter: { sales_id: identity?.id, "done_date@not.is": null },
      // NEWEST COMPLETION FIRST. Sorting by due date, which is what the open
      // list does, would put a task due last year but closed this morning at
      // the top of a list about what happened recently -- or rather at the
      // bottom of it, which is worse.
      sort: { field: "done_date", order: "DESC" },
      pagination: { page: 1, perPage: COMPLETED_TASKS_FETCHED },
    },
    { enabled: !!identity },
  );

  /*
   * A DENIED READ IS NOT AN EMPTY HISTORY. This tab exists to answer "did we
   * actually follow up?", and "nothing" is the one answer it must never give
   * by accident.
   */
  if (error)
    return (
      <p className="text-sm text-destructive">
        Could not load completed tasks, so this is not a record of nothing
        having been done — it is a record of not knowing.
      </p>
    );

  if (isPending)
    return <p className="text-sm text-muted-foreground">Loading…</p>;

  if (!data?.length)
    return (
      <p className="text-sm text-muted-foreground">
        Nothing completed yet. Ticking a task off the Open tab puts it here —
        and it stays here, which is the point: a closed task used to disappear
        from the app entirely five minutes after it was ticked.
      </p>
    );

  return (
    <div className="flex flex-col gap-3">
      <TaskRows tasks={data} showContact />
      {(total ?? 0) > data.length && (
        <p className="text-xs text-muted-foreground">
          Showing the {data.length} most recently completed of {total}.
        </p>
      )}
    </div>
  );
};

/**
 * The desktop screen, registered as the `tasks` resource's list.
 *
 * No shell of its own: layout/Layout.tsx already gives every desktop page its
 * column, and this is a resource route rather than one of ours, so it does not
 * pass through the ScreenFrame that marketing/mobile.tsx maps over its own
 * route array.
 */
export const AllTasksScreen = () => (
  <div className="flex flex-col gap-3 pb-8">
    <h1 className="text-xl font-semibold">Tasks</h1>
    <p className="text-sm text-muted-foreground">
      Your own queue. Open is what is on you; Completed is what was actually
      done, which nothing in this app could show before.
    </p>
    <AllTasksBody />
  </div>
);

/**
 * The same screen on a phone.
 *
 * A SEPARATE EXPORT RATHER THAN A `useIsMobile()` BRANCH. root/CRM.tsx already
 * decides which of two <Admin> trees to render, so inside the mobile one the
 * answer is known before the first paint -- whereas `useIsMobile` starts
 * `false` and corrects itself in an effect, which paints the desktop heading
 * inside the mobile layout on every mount. It also needs MobileHeader, whose
 * fixed bar MobileContent's `pt-18` exists to clear; that pairing is upstream's
 * and is why this is a wrapper rather than a class.
 */
export const MobileAllTasks = () => {
  const translate = useTranslate();
  return (
    <>
      <MobileHeader>
        <h1 className="text-xl font-semibold">
          {translate("resources.tasks.name", { smart_count: 2 })}
        </h1>
      </MobileHeader>
      <MobileContent>
        <AllTasksBody />
      </MobileContent>
    </>
  );
};

/**
 * The way in, from the dashboard's "Upcoming Tasks" card.
 *
 * WITHOUT THIS THE DESKTOP SCREEN IS UNREACHABLE, which is the failure
 * marketing/mobile.tsx warns about in capitals: a route without a link is a
 * screen nobody can open, and it has already happened once in this fork for a
 * whole session. The phone needs no equivalent -- upstream's bottom bar has
 * had a Tasks slot pointing at this path all along; it is the desktop header,
 * which hardcodes four tabs, that offers no way there.
 *
 * IN THE DASHBOARD CARD RATHER THAN IN THE HEADER. The card is already the
 * desktop's task surface and already says "Upcoming", which is exactly the
 * word that raises the question this link answers. A fifth top-level tab would
 * put a personal queue beside Contacts, Companies and Deals.
 */
export const AllTasksLink = () => (
  <Button
    asChild
    variant="link"
    size="sm"
    className="h-auto p-0 text-xs text-muted-foreground underline hover:no-underline"
  >
    <Link to={ALL_TASKS_PATH}>See all tasks, open and completed</Link>
  </Button>
);
